import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { ApiResponse } from '../../shared/types';
import { LocationService } from '../../services/location.service';
import {
  Employee,
  EmployeeResponse,
  EmployeeApiResponse,
  Shift,
  CreateShiftRequest,
  CreateShiftResponse,
  ShiftSchedule,
  ShiftScheduleApiResponse,
  ShiftScheduleFilters,
  UpdateEmployeeData,
  RewardSettings,
  RewardSettingsApiResponse,
  UpdateRewardSettingsRequest,
  EmployeeReward,
} from '../models/employee.models';
import { LoggerService } from '../../services/logger.service';
import { MultiSelectOption } from '../../shared/components/wi-multi-select/wi-multi-select.component';
import { getUserTimezone } from '../../shared/utils/date.util';
import { PhoneValidator } from '../../shared/validators/phone.validator';

export interface EmployeeData {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  /** Attendant only — omit for staff. */
  default_opening_balance?: number;
  username: string;
  password: string;
  designation: string;
  role: 'attendant' | 'attendant_plus' | 'staff';
  start_date: string;
  link: string;
  location_id: string;
  emp_status?: boolean;
  picture_url?: File;
}

export interface UpdateEmployeeResponse {
  status: boolean;
  status_code: number;
  message: string;
  data: Employee;
  errors?: Record<string, string | string[]>;
}

/**
 * EmployeeService - Centralized Angular injectable service for managing employees, shifts, schedules, rewards, and related utilities.
 * @component
 * @selector employee-service
 * @example
 *   <employee-service [apiService]="api" [locationService]="locationService"></employee-service>
 * @input {ApiService} apiService - Injected HTTP/API wrapper used to perform all backend requests (GET, POST, PUT, DELETE).
 * @input {LocationService} locationService - Injected helper/service for location-related logic and lookups.
 * @output {Observable<EmployeeResponse>} loadEmployees - Emits paginated employee lists based on page, limit, search and filter criteria.
 * @output {Observable<Shift[]>} loadShifts - Emits the full list of shifts.
 * @output {Observable<Shift[]>} loadShiftsWithSearch - Emits shift list matching a search term.
 * @output {Observable<CreateShiftResponse>} createShift - Emits API response after creating a shift.
 * @output {Observable<any>} getShiftDetails - Emits shift details for a given shift ID.
 * @output {Observable<any>} updateShift - Emits API response after updating a shift.
 * @output {Observable<any>} deleteShift - Emits API response after deleting a shift.
 * @output {Observable<any>} createEmployee - Emits API response after creating an employee (supports file uploads).
 * @output {Observable<UpdateEmployeeResponse>} updateEmployee - Emits API response after updating an employee (handles FormData when files are present).
 * @output {Observable<any>} deleteEmployee - Emits API response after deleting an employee.
 * @output {Observable<{data: ShiftSchedule[], pagination?: any}>} loadShiftSchedules - Emits shift schedule data and optional pagination metadata filtered by date range/search/pagination.
 * @output {Observable<RewardSettings>} getRewardSettings - Emits current reward settings.
 * @output {Observable<RewardSettings>} updateRewardSettings - Emits updated reward settings after saving.
 * @output {Observable<any>} scheduleShift - Emits API response after scheduling shifts for employees.
 * @output {Observable<ApiResponse<EmployeeReward[]>>} getRewardStatistics - Emits reward statistics for a date range (optionally filtered by search).
 * @output {Observable<any>} getEmployeeStatistics - Emits aggregated employee statistics (includes client timezone query param).
 * @output {Observable<any>} getEmployeeSchedule - Emits schedule data for a specific employee schedule ID and week range.
 * @output {Observable<any>} updateEmployeeSchedule - Emits API response after updating a schedule.
 *
 * Purpose:
 *   - Provide a single, injectable service that encapsulates all employee-related API interactions, data transformations, and lightweight utilities used across the application.
 *   - Handle paging, filtering (search, status, location), file uploads (FormData), and consistent response validation and error handling.
 *
 * Key methods (brief):
 *   - loadEmployees(page, limit, search?, filters?) : Observable<EmployeeResponse>
 *       Fetches a paginated list of employees; supports search, status filter (active/inactive) and multiple location IDs.
 *   - loadShifts() / loadShiftsWithSearch(searchTerm?) : Observable<Shift[]>
 *       Retrieve shifts and support simple search.
 *   - createShift(shiftData) / getShiftDetails(shiftId) / updateShift(shiftId, shiftData)
 *       CRUD operations for shift resources.
 *   - createEmployee(employeeData) / updateEmployee(employeeId, employeeData) / deleteEmployee(employeeId)
 *       Create, update (with optional file upload handling), and delete employee records.
 *   - loadShiftSchedules(filters?) : Observable<{data, pagination?}>
 *       Fetch schedules with optional search, week range and pagination parameters.
 *   - getRewardSettings() / updateRewardSettings(settings)
 *       Read and persist reward settings.
 *   - scheduleShift(scheduleData)
 *       Bulk schedule employees for a given week range and shift.
 *   - getRewardStatistics(startDate, endDate, search?) / getEmployeeStatistics()
 *       Retrieve analytics and reporting endpoints; employee statistics sends timezone for server-side date boundaries.
 *   - getEmployeeSchedule(scheduleId, weekStart, weekEnd) / updateEmployeeSchedule(scheduleId, scheduleData)
 *       Fetch and update individual employee schedule records.
 *
 * Utility methods:
 *   - getPaginationInfo(currentPage, itemsPerPage, totalItems) : string
 *       Returns a human readable range like "Items 1 - 10 of 31".
 *   - getEmployeeInitials(firstName, lastName) : string
 *       Returns uppercase initials (e.g., "JD").
 *   - formatPhoneNumber(phoneNumber) : string
 *       Basic formatting for 10-digit numbers, falls back to raw input.
 *   - getEmployeeFullName(employee) : string
 *       Concatenate first and last name.
 *   - getEmployeeId(employee) : string
 *       Returns employee.emp_id or 'N/A'.
 *
 * Error handling:
 *   - Methods validate API response shape (status and data), log raw responses for debugging, and throw meaningful errors when processing fails.
 *
 * Lifecycle:
 *   - Provided in root via @Injectable({ providedIn: 'root' }); no Angular component lifecycle hooks are used inside this service.
 */
@Injectable({
  providedIn: 'root',
})
export class EmployeeService {
  private readonly logger = inject(LoggerService);

  constructor(
    private apiService: ApiService,
    private locationService: LocationService
  ) {}

  /**
   * Normalizes list payload: legacy `data.data`, newer `data.employees` / `data.employee`,
   * or `data` / root as a bare array.
   */
  private extractEmployeeListRows(payload: any): Employee[] {
    if (Array.isArray(payload)) {
      return payload as Employee[];
    }
    if (!payload || typeof payload !== 'object') {
      return [];
    }
    const o = payload as Record<string, any>;
    const keys = ['employees', 'employee', 'data', 'items', 'results', 'list'] as const;
    for (const k of keys) {
      const v = o[k];
      if (Array.isArray(v)) {
        return v as Employee[];
      }
    }
    return [];
  }

  /**
   * Load employees from the API with pagination, optional search, and optional location/status filters, returning mapped employee data and pagination metadata.
   * @example
   * oadEmployees(1, 10, 'john', { locations: ['1','2'], status: 'active' })
   * Observable<{ employees: Employee[], totalItems: number, currentPage: number, totalPages: number, itemsPerPage: number }>
   * @param {number} page - Page number to retrieve.
   * @param {number} limit - Number of items per page.
   * @param {string} [search] - Optional search term to filter employees.
   * @param {{ locations?: string[], status?: ('active'|'inactive'|null), includeAdmin?: boolean }} [filters] - Optional filters: array of location IDs, employee status, and whether to include the shop admin row.
   * @returns {Observable<EmployeeResponse>} Observable that emits the employees list and pagination information.
   **/
  loadEmployees(
    page: number,
    limit: number,
    search?: string,
    filters?: {
      locations?: string[];
      status?: 'active' | 'inactive' | null;
      includeAdmin?: boolean;
      includeAttendanceStatus?: boolean;
    }
  ): Observable<EmployeeResponse> {
    let endpoint = `employee?page=${page}&limit=${limit}`;

    this.logger.debug('Employee service - filters received:', filters);

    if (search && search.trim()) {
      endpoint += `&search=${encodeURIComponent(search.trim())}`;
    }

    // Add status filter - using the API format from your example
    if (filters?.status) {
      endpoint += `&emp_status=${filters.status === 'active'}`;
    }

    // Add location filter using location_id - supporting multiple IDs in comma-separated format
    if (filters?.locations && filters.locations.length > 0) {
      // Join multiple location IDs with commas as per your API example
      const locationIds = filters.locations.join(',');
      this.logger.debug('Adding location filter - locationIds:', locationIds);
      endpoint += `&location_id=${locationIds}`;
    }

    if (filters?.includeAdmin) {
      endpoint += '&include_admin=true';
    }

    if (filters?.includeAttendanceStatus) {
      endpoint += `&includeAttendanceStatus=true&timezone=${encodeURIComponent(getUserTimezone())}`;
    }

    this.logger.debug('Final API endpoint:', endpoint);

    return this.apiService.get<EmployeeApiResponse>(endpoint).pipe(
      map((response: any) => {
        this.logger.debug('Raw API response:', response);
        try {
          if (response.status && response.data) {
            const { data } = response;
            const employees = this.extractEmployeeListRows(data);
            const paginationRaw =
              data && typeof data === 'object' && !Array.isArray(data)
                ? (data as Record<string, any>)['pagination']
                : undefined;
            const pagination =
              paginationRaw && typeof paginationRaw === 'object' && !Array.isArray(paginationRaw)
                ? (paginationRaw as Record<string, any>)
                : {};

            if (!paginationRaw) {
              this.logger.warn(
                'Employee list API: pagination block missing; using row count and request page/limit (verify backend contract).'
              );
            }

            const pageSize = Number(pagination['limit'] ?? limit) || limit;
            const total = Number(pagination['total'] ?? employees.length) || employees.length;
            const totalPages =
              Number(pagination['totalPages']) || Math.max(1, Math.ceil(total / (pageSize || 1)));
            const currentPage = Number(pagination['page'] ?? page) || page;

            const result = {
              employees,
              totalItems: total,
              currentPage,
              totalPages,
              itemsPerPage: pageSize,
            };

            this.logger.debug('Employee API Response - Pagination:', {
              total,
              totalPages: result.totalPages,
              page: currentPage,
              limit: pageSize,
              rowCount: employees.length,
            });
            this.logger.debug('Returning to component:', result);

            return result;
          }
          this.logger.error('API response error:', response);
          throw new Error(response.message || 'Failed to load employees');
        } catch (error) {
          this.logger.error('Error processing employee response:', error);
          throw new Error('Failed to process employee data');
        }
      })
    );
  }

  getEmployeeOptions(): Observable<MultiSelectOption[]> {
    return this.loadEmployees(1, 1000, '', { locations: [], status: null }).pipe(
      map(res =>
        (res.employees || []).map(emp => ({
          id: emp.id,
          name: `${emp.first_name} ${emp.last_name}`,
        }))
      )
    );
  }

  /**
   * Loads shifts for the current employee from the API and returns an Observable of Shift objects.
   * @example
   * oadShifts().subscribe(shifts => this.logger.debug(shifts))
   * [ { id: 123, startTime: '2026-03-01T09:00:00Z', endTime: '2026-03-01T17:00:00Z' } ]
   * @returns {{Observable<Shift[]>}} Observable that emits an array of Shift objects.
   **/
  loadShifts(): Observable<Shift[]> {
    return this.apiService.get<Shift[]>('employee/shift').pipe(
      map((response: any) => {
        this.logger.debug('Raw shifts API response:', response);
        try {
          if (response.status && response.data) {
            this.logger.debug('Processed shifts response:', response.data);
            return response.data;
          }
          this.logger.error('Shifts API response error:', response);
          throw new Error(response.message || 'Failed to load shifts');
        } catch (error) {
          this.logger.error('Error processing shifts response:', error);
          throw new Error('Failed to process shifts data');
        }
      })
    );
  }

  /**
   * Load shifts from the employee API, optionally filtered by a search term; returns an observable of Shift arrays.
   * @example
   * oadShiftsWithSearch('morning')
   * [{ id: 1, title: 'Morning Shift', start: '08:00', end: '16:00' }]
   * @param {string} searchTerm - Optional search term to filter shifts; defaults to an empty string.
   * @returns {Observable<Shift[]>} Observable that emits an array of Shift objects when the API call succeeds.
   **/
  loadShiftsWithSearch(searchTerm = ''): Observable<Shift[]> {
    const url = searchTerm.trim()
      ? `employee/shift?search=${encodeURIComponent(searchTerm.trim())}`
      : 'employee/shift';
    return this.apiService.get<Shift[]>(url).pipe(
      map((response: any) => {
        this.logger.debug('Raw shifts search API response:', response);
        try {
          if (response.status && response.data) {
            this.logger.debug('Processed shifts search response:', response.data);
            return response.data;
          }
          this.logger.error('Shifts search API response error:', response);
          throw new Error(response.message || 'Failed to search shifts');
        } catch (error) {
          this.logger.error('Error processing shifts search response:', error);
          throw new Error('Failed to process shifts search data');
        }
      })
    );
  }

  /**
   * Creates a new employee shift by POSTing the provided shift data to the backend and returns the API response as an observable.
   * @example
   * createShift({ employeeId: '123', startTime: '2026-03-17T09:00:00Z', endTime: '2026-03-17T17:00:00Z' })
   * Observable<CreateShiftResponse>
   * @param {{CreateShiftRequest}} {{shiftData}} - Shift creation payload containing details such as employee ID, start/end times and any relevant metadata.
   * @returns {{Observable<CreateShiftResponse}} Observable that emits the created shift response; will error if the API response is invalid or processing fails.
   **/
  createShift(shiftData: CreateShiftRequest): Observable<CreateShiftResponse> {
    return this.apiService.post<CreateShiftResponse>('employee/shift', shiftData).pipe(
      map((response: any) => {
        this.logger.debug('Raw create shift API response:', response);
        try {
          if (response.status && response.data) {
            this.logger.debug('Processed create shift response:', response);
            return response;
          }
          this.logger.error('Create shift API response error:', response);
          throw new Error(response.message || 'Failed to create shift');
        } catch (error) {
          this.logger.error('Error processing create shift response:', error);
          throw new Error('Failed to process shift creation');
        }
      })
    );
  }

  /**
   * Fetches details for a specific employee shift by ID and returns an observable of the API response.
   * @example
   * etShiftDetails('shift123')
   * // Observable that emits the API response, e.g. { status: true, data: { /* shift details */
  getShiftDetails(shiftId: string): Observable<any> {
    return this.apiService.get<any>(`employee/shift/${shiftId}`).pipe(
      map((response: any) => {
        this.logger.debug('Raw get shift details API response:', response);
        try {
          if (response.status && response.data) {
            this.logger.debug('Processed shift details response:', response);
            return response;
          }
          this.logger.error('Get shift details API response error:', response);
          throw new Error(response.message || 'Failed to fetch shift details');
        } catch (error) {
          this.logger.error('Error processing shift details response:', error);
          throw new Error('Failed to process shift details');
        }
      })
    );
  }

  /**
   * Update an existing employee shift by ID via the API and return an observable of the API response.
   * @example
   * pdateShift('shift123', { startTime: '09:00', endTime: '17:00' })
   * Observable<{ status: string, data: any }>
   * @param {{string}} {{shiftId}} - ID of the shift to update.
   * @param {{CreateShiftRequest}} {{shiftData}} - Payload containing the updated shift details.
   * @returns {{Observable<any>}} Observable that emits the API response object on success or an error on failure.
   **/
  updateShift(shiftId: string, shiftData: CreateShiftRequest): Observable<any> {
    return this.apiService.put<any>(`employee/shift/${shiftId}`, shiftData).pipe(
      map((response: any) => {
        this.logger.debug('Raw update shift API response:', response);
        try {
          if (response.status && response.data) {
            this.logger.debug('Processed update shift response:', response);
            return response;
          }
          this.logger.error('Update shift API response error:', response);
          throw new Error(response.message || 'Failed to update shift');
        } catch (error) {
          this.logger.error('Error processing update shift response:', error);
          throw new Error('Failed to process shift update');
        }
      })
    );
  }

  getPaginationInfo(currentPage: number, itemsPerPage: number, totalItems: number): string {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalItems);
    return `Items ${start} - ${end} of ${totalItems}`;
  }

  getEmployeeInitials(firstName: string, lastName: string): string {
    const first = firstName.charAt(0).toUpperCase();
    const last = lastName.charAt(0).toUpperCase();
    return `${first}${last}`;
  }

  formatPhoneNumber(phoneNumber: string | null | undefined): string {
    if (phoneNumber == null || String(phoneNumber).trim() === '') {
      return '';
    }
    return PhoneValidator.formatUsaPhoneDisplay(phoneNumber);
  }

  getEmployeeFullName(employee: Employee): string {
    return `${employee.first_name} ${employee.last_name}`;
  }

  getEmployeeId(employee: Employee): string {
    // Use emp_id directly from API response, no generation
    return employee.emp_id || 'N/A';
  }

  deleteEmployee(employeeId: string): Observable<any> {
    return this.apiService.delete(`employee/${employeeId}`);
  }

  deleteShift(shiftId: string): Observable<any> {
    return this.apiService.delete(`employee/shift/${shiftId}`);
  }

  /**
   * Create a new employee by sending multipart/form-data to the employee API and returning the API response as an Observable.
   * @example
   * createEmployee({ first_name: 'Jane', email: 'jane@example.com', password: 'secret', username: 'jane.doe' })
   * Observable<any>
   * @param {{EmployeeData}} {{employeeData}} - Employee data including fields like first_name, last_name, email, password, username, designation, phone_number, start_date, location_id, picture_url, and emp_status.
   * @returns {{Observable<any>}} Return an Observable that emits the API response object on success or throws an error on failure.
   **/
  createEmployee(employeeData: EmployeeData): Observable<any> {
    // Create FormData to match the new API structure
    const formData = new FormData();
    formData.append('first_name', employeeData.first_name);
    formData.append('email', employeeData.email);
    formData.append('password', employeeData.password);
    formData.append('username', employeeData.username);
    formData.append('designation', employeeData.designation);
    formData.append('role', employeeData.role);
    formData.append('phone_number', employeeData.phone_number);
    if (
      employeeData.default_opening_balance !== undefined &&
      employeeData.default_opening_balance !== null
    ) {
      formData.append('default_opening_balance', employeeData.default_opening_balance.toString());
    }
    formData.append('start_date', employeeData.start_date);
    formData.append('link', employeeData.link);
    formData.append('location_id', employeeData.location_id);
    formData.append('last_name', employeeData.last_name);
    formData.append(
      'emp_status',
      (employeeData.emp_status !== undefined ? employeeData.emp_status : true).toString()
    );

    // Add profile photo if provided
    if (employeeData.picture_url) {
      formData.append('picture_url', employeeData.picture_url, employeeData.picture_url.name);
    }

    return this.apiService.post<any>('employee', formData).pipe(
      map((response: any) => {
        this.logger.debug('Raw create employee API response:', response);
        try {
          if (response.status && response.status_code === 200) {
            this.logger.debug('Processed create employee response:', response);
            return response;
          }
          this.logger.error('Create employee API response error:', response);
          throw new Error(response.message || 'Failed to create employee');
        } catch (error) {
          this.logger.error('Error processing create employee response:', error);
          throw new Error('Failed to process employee creation');
        }
      })
    );
  }

  /**
   * Update an employee's data, using FormData when picture_url is a File to handle file upload.
   * @example
   * pdateEmployee('employeeId123', { first_name: 'Jane', last_name: 'Doe', email: 'jane@example.com', phone_number: '1234567890', designation: 'Engineer', location_id: 'loc1', start_date: '2020-01-01', emp_status: 1, username: 'jdoe', password: 'secret', picture_url: new File([], 'photo.jpg') })
   * Observable<UpdateEmployeeResponse>
   * @param {{string}} {{employeeId}} - The ID of the employee to update.
   * @param {{UpdateEmployeeData}} {{employeeData}} - Employee data to update; picture_url may be a File to trigger multipart/form-data upload.
   * @returns {{Observable<UpdateEmployeeResponse>}} Observable that emits the update response.
   **/
  updateEmployee(
    employeeId: string,
    employeeData: UpdateEmployeeData
  ): Observable<UpdateEmployeeResponse> {
    // Check if there's a file to upload
    const hasFile = employeeData.picture_url instanceof File;

    if (hasFile) {
      // Use FormData for file uploads
      const formData = new FormData();
      formData.append('first_name', employeeData.first_name);
      formData.append('last_name', employeeData.last_name);
      formData.append('email', employeeData.email);
      formData.append('phone_number', employeeData.phone_number);
      if (
        employeeData.default_opening_balance !== undefined &&
        employeeData.default_opening_balance !== null
      ) {
        formData.append('default_opening_balance', employeeData.default_opening_balance.toString());
      }
      formData.append('designation', employeeData.designation);
      formData.append('role', employeeData.role);
      formData.append('location_id', employeeData.location_id);
      formData.append('start_date', employeeData.start_date);
      formData.append('emp_status', employeeData.emp_status.toString());
      formData.append('username', employeeData.username);

      // Add password only if provided
      if (employeeData.password) {
        formData.append('password', employeeData.password);
      }

      // Add profile photo if it exists
      if (employeeData.picture_url) {
        formData.append('picture_url', employeeData.picture_url, employeeData.picture_url.name);
      }

      return this.apiService
        .put<UpdateEmployeeResponse>(`employee/${employeeId}`, formData)
        .pipe(map((response: any) => response as UpdateEmployeeResponse));
    }
    // Use regular JSON for updates without files
    return this.apiService
      .put<UpdateEmployeeResponse>(`employee/${employeeId}`, employeeData)
      .pipe(map((response: any) => response as UpdateEmployeeResponse));
  }

  /**
   * Loads shift schedules from the employee/schedule endpoint applying optional search and pagination filters.
   * @example
   * oadShiftSchedules({ search: 'john', page: 1, limit: 10 })
   * { data: [{ id: 1, employee_id: 123, start: '2026-03-01T08:00:00Z', end: '2026-03-01T16:00:00Z' }], pagination: { page: 1, limit: 10, total: 42 } }
   * @param {{ShiftScheduleFilters}} {{filters}} - Optional filters to search and paginate shift schedules (search, week_start, week_end, page, limit).
   * @returns {{Observable<{data: ShiftSchedule[], pagination?: any}>}} Observable that emits an object with an array of ShiftSchedule items and optional pagination metadata.
   **/
  loadShiftSchedules(
    filters?: ShiftScheduleFilters
  ): Observable<{ data: ShiftSchedule[]; pagination?: any }> {
    let endpoint = 'employee/schedule';
    const queryParams: string[] = [];

    this.logger.debug('EmployeeService.loadShiftSchedules called with filters:', filters);

    if (filters?.search && filters.search.trim()) {
      queryParams.push(`search=${encodeURIComponent(filters.search.trim())}`);
    }

    if (filters?.week_start) {
      queryParams.push(`week_start=${filters.week_start}`);
    }

    if (filters?.week_end) {
      queryParams.push(`week_end=${filters.week_end}`);
    }

    // Add pagination parameters
    if (filters?.page) {
      queryParams.push(`page=${filters.page}`);
    }

    if (filters?.limit) {
      queryParams.push(`limit=${filters.limit}`);
    }

    if (queryParams.length > 0) {
      endpoint += `?${queryParams.join('&')}`;
    }

    this.logger.debug('Final API endpoint:', endpoint);

    return this.apiService.get<ShiftScheduleApiResponse>(endpoint).pipe(
      map((response: any) => {
        this.logger.debug('Raw shift schedules API response:', response);
        try {
          if (response.status && response.data) {
            this.logger.debug('Processed shift schedules response:', response.data);
            // API structure: { status: true, data: { data: [...], pagination: {...} } }
            const pagination = response.data.pagination || response.pagination;
            const data = response.data.data || response.data || [];
            this.logger.debug('Pagination metadata:', pagination);
            return {
              data: data,
              pagination: pagination,
            };
          }
          this.logger.error('Shift schedules API response error:', response);
          throw new Error(response.message || 'Failed to load shift schedules');
        } catch (error) {
          this.logger.error('Error processing shift schedules response:', error);
          throw new Error('Failed to process shift schedules data');
        }
      })
    );
  }

  // Reward Settings API methods
  /**
   * Fetches reward settings for the current employee from the backend and returns them as an observable.
   * @example
   * etRewardSettings()
   * Observable<RewardSettings>
   * @param {void} none - No parameters.
   * @returns {Observable<RewardSettings>} An observable that emits the employee's RewardSettings object.
   **/
  getRewardSettings(): Observable<RewardSettings> {
    return this.apiService.get<RewardSettingsApiResponse>('employee/reward-settings').pipe(
      map((response: any) => {
        this.logger.debug('Raw reward settings API response:', response);
        try {
          if (response.status && response.data) {
            this.logger.debug('Processed reward settings response:', response.data);
            return response.data;
          }
          this.logger.error('Reward settings API response error:', response);
          throw new Error(response.message || 'Failed to load reward settings');
        } catch (error) {
          this.logger.error('Error processing reward settings response:', error);
          throw new Error('Failed to process reward settings data');
        }
      })
    );
  }

  /**
   * Update employee reward settings via API and return the updated settings as an observable.
   * @example
   * pdateRewardSettings({ employeeId: '123', pointsEnabled: true })
   * Observable<RewardSettings>
   * @param {{UpdateRewardSettingsRequest}} {{settings}} - Request payload containing reward settings to update.
   * @returns {{Observable<RewardSettings>}} Return an observable that emits the updated reward settings.
   **/
  updateRewardSettings(settings: UpdateRewardSettingsRequest): Observable<RewardSettings> {
    return this.apiService
      .post<RewardSettingsApiResponse>('employee/reward-settings', settings)
      .pipe(
        map((response: any) => {
          this.logger.debug('Raw update reward settings API response:', response);
          try {
            if (response.status && response.data) {
              this.logger.debug('Processed update reward settings response:', response.data);
              return response.data;
            }
            this.logger.error('Update reward settings API response error:', response);
            throw new Error(response.message || 'Failed to update reward settings');
          } catch (error) {
            this.logger.error('Error processing update reward settings response:', error);
            throw new Error('Failed to process reward settings update');
          }
        })
      );
  }

  // Schedule Shift API
  /**
   * Sends schedule data to the backend to assign employees to a specific shift for a given week.
   * @example
   * cheduleShift({ employee_ids: ['e1','e2'], shift_id: 'shift123', week_start: '2026-03-01', week_end: '2026-03-07' })
   * Observable<{ status: boolean; status_code: number; data?: any }>
   * @param {{ { employee_ids: string[]; shift_id: string; week_start: string; week_end: string } }} scheduleData - Object containing employee IDs, the shift identifier, and the week start/end dates used to schedule the shift.
   * @returns {{Observable<any>}} Observable<any> that emits the API response object when scheduling succeeds or errors if scheduling fails.
   **/
  scheduleShift(scheduleData: {
    employee_ids: string[];
    shift_id: string;
    week_start: string;
    week_end: string;
  }): Observable<any> {
    return this.apiService.post<any>('employee/schedule', scheduleData).pipe(
      map((response: any) => {
        this.logger.debug('Raw schedule shift API response:', response);
        try {
          if (response.status && response.status_code === 200) {
            this.logger.debug('Processed schedule shift response:', response);
            return response;
          }
          this.logger.error('Schedule shift API response error:', response);
          throw new Error(response.message || 'Failed to schedule shift');
        } catch (error) {
          this.logger.error('Error processing schedule shift response:', error);
          throw new Error('Failed to process shift scheduling');
        }
      })
    );
  }

  // Reward Statistics API
  /**
   * Fetches employee reward statistics for a given date range, optionally filtered by a search term.
   * @example
   * etRewardStatistics('2023-01-01', '2023-01-31', 'john.doe')
   * // Observable<ApiResponse<EmployeeReward[]>>
   * @param {string} startDate - Start date (ISO string) for the statistics range.
   * @param {string} endDate - End date (ISO string) for the statistics range.
   * @param {string} [search] - Optional search term to filter employee rewards.
   * @returns {Observable<ApiResponse<EmployeeReward[]>>} Observable that emits an API response containing an array of EmployeeReward.
   */
  getRewardStatistics(
    startDate: string,
    endDate: string,
    search?: string
  ): Observable<ApiResponse<EmployeeReward[]>> {
    const params: any = {
      start_date: startDate,
      end_date: endDate,
    };

    // Add search parameter if provided
    if (search && search.trim()) {
      params.search = search.trim();
    }

    return this.apiService.get<EmployeeReward[]>('employee/reward-statistics-by-admin', params);
  }

  // Get employee statistics from API
  getEmployeeStatistics(): Observable<any> {
    return this.apiService.get<any>('employee/statistics', { timezone: getUserTimezone() });
  }

  // Get schedule data for a specific employee and schedule ID
  getEmployeeSchedule(scheduleId: string, weekStart: string, weekEnd: string): Observable<any> {
    const params = {
      week_start: weekStart,
      week_end: weekEnd,
    };
    return this.apiService.get<any>(`employee/schedule/${scheduleId}`, params);
  }

  // Update schedule data
  updateEmployeeSchedule(scheduleId: string, scheduleData: any): Observable<any> {
    return this.apiService.put<any>(`employee/schedule/${scheduleId}`, scheduleData);
  }
}
